/**
 * Product Card Dynamic Options
 * Handles size/color option switching and add to cart functionality
 */

class ProductCardManager {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.bindEvents();
    });
  }

  bindEvents() {
    // Size option changes
    this.delegateEvent('.product-card__option', 'click', (element, productCard) => {
      const optionName = element.closest('[data-option-name]').getAttribute('data-option-name');
      const optionValue = element.getAttribute('data-option-value');
      
      this.updateSelection(element);
      
      if (optionName === 'Size') {
        this.updateSizeValue(productCard, optionValue);
      }
    });

    // Color swatch changes
    this.delegateEvent('.product-card__swatch', 'click', (element, productCard) => {
      const optionValue = element.getAttribute('data-option-value');
      this.updateSelection(element, '.product-card__swatch');
      this.updateProductForColor(productCard, optionValue);
    });

    // Buy button clicks
    this.delegateEvent('.product-card__btn.buy', 'click', (element, productCard, event) => {
      event.preventDefault();
      event.stopPropagation();
      
      const selectedVariant = this.getSelectedVariant(productCard);
      if (selectedVariant) {
        this.addVariantToCart(selectedVariant.id, 1);
      } else {
        console.error('No variant selected or available');
      }
    });
  }

  delegateEvent(selector, eventType, handler) {
    document.addEventListener(eventType, (event) => {
      const element = event.target.closest(selector);
      if (element) {
        const productCard = element.closest('.product-card__container');
        if (productCard) {
          handler(element, productCard, event);
        }
      }
    });
  }

  updateSelection(element, siblingSelector = null) {
    const container = siblingSelector 
      ? element.closest('.product-card__color-swatches')
      : element.parentNode;
    
    const targetSelector = siblingSelector || '.product-card__option';
    
    // Remove selected from siblings
    container.querySelectorAll(targetSelector).forEach(sibling => {
      sibling.classList.remove('selected');
    });
    
    // Add selected to current element
    element.classList.add('selected');
  }

  getVariantsData(productCard) {
    const variantsScript = productCard.querySelector('script[type="application/json"]');
    if (!variantsScript) {
      console.error('Product variants JSON not found');
      return null;
    }
    
    try {
      return JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.error('Error parsing variants JSON:', error);
      return null;
    }
  }

  getSelectedOptions(productCard) {
    const options = {};
    
    // Get selected size
    const sizeOption = productCard.querySelector('.product-card__options .product-card__option.selected');
    if (sizeOption) {
      options.Size = sizeOption.getAttribute('data-option-value');
    }
    
    // Get selected color
    const colorSwatch = productCard.querySelector('.product-card__color-swatches .product-card__swatch.selected');
    if (colorSwatch) {
      options.Color = colorSwatch.getAttribute('data-option-value');
    }
    
    return options;
  }

  findVariantByOptions(variants, selectedOptions) {
    return variants.find(variant => {
      return Object.keys(selectedOptions).every(optionName => {
        const selectedValue = selectedOptions[optionName];
        const optionIndex = optionName === 'Size' ? 0 : optionName === 'Color' ? 1 : -1;
        
        if (optionIndex === -1) return true;
        
        return variant.options && variant.options[optionIndex] === selectedValue;
      });
    });
  }

  updateSizeValue(productCard, selectedSize) {
    const variants = this.getVariantsData(productCard);
    if (!variants) return;
    
    const matchingVariant = variants.find(variant => {
      return variant.options && variant.options.some(option => option === selectedSize);
    });
    
    if (matchingVariant && matchingVariant.size_metafield) {
      const sizeValueElement = productCard.querySelector('.variant-size-value');
      if (sizeValueElement) {
        sizeValueElement.textContent = matchingVariant.size_metafield;
      }
    }
  }

  async updateProductForColor(productCard, selectedColor) {
    const variants = this.getVariantsData(productCard);
    if (!variants) return;
    
    const matchingVariant = variants.find(variant => {
      return variant.options && variant.options.some(option => option === selectedColor);
    });
    
    if (!matchingVariant) return;
    
    // Update product image
    if (matchingVariant.featured_image) {
      const productImage = productCard.querySelector('.product-card__product-image img');
      if (productImage) {
        const imageUrl = matchingVariant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '_600x.$1');
        productImage.src = imageUrl;
        productImage.alt = matchingVariant.featured_image.alt || 'Product image';
      }
    }
    
    // Update price if different
    const priceElement = productCard.querySelector('.product-card__product-price');
    if (priceElement && matchingVariant.price) {
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(matchingVariant.price / 100);
      priceElement.textContent = formattedPrice;
    }
  }

  getSelectedVariant(productCard) {
    const variants = this.getVariantsData(productCard);
    if (!variants) return null;
    
    const selectedOptions = this.getSelectedOptions(productCard);
    const matchingVariant = this.findVariantByOptions(variants, selectedOptions);
    
    return matchingVariant || null;
  }

  async addVariantToCart(variantId, quantity) {
    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      
      // Get cart sections to update
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
      }
      
      config.body = formData;

      const response = await fetch(routes.cart_add_url, config);
      const result = await response.json();
      
      if (result.status) {
        this.handleCartError(variantId, result);
      } else {
        this.handleCartSuccess(variantId, result);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleCartError(variantId, { errors: 'Network error' });
    }
  }

  handleCartError(variantId, result) {
    publish(PUB_SUB_EVENTS.cartError, {
      source: 'product-card',
      productVariantId: variantId,
      errors: result.errors || result.description,
      message: result.message || 'Unable to add product to cart',
    });
    console.error('Failed to add to cart:', result);
  }

  handleCartSuccess(variantId, result) {
    // Use the shared cart handler utilities for consistent behavior
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    
    if (window.CartHandlerUtils) {
      window.CartHandlerUtils.handleAddResponse(result, variantId, {
        source: 'product-card',
        cart: cart,
        error: false
      });
    } else {
      // Fallback to original behavior if utilities not loaded
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-card',
        productVariantId: variantId,
        cartData: result,
      });
      
      if (cart && cart.renderContents) {
        cart.renderContents(result);
        cart.classList.remove('is-empty');
      }
    }
  }
}

// Initialize the product card manager
new ProductCardManager();
